import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Card } from '@outdoorly/card';
import {
  Box,
  Flex,
  Skeleton,
  Tabs,
  TabList,
  Tab,
  StatLabel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Divider,
  Center,
  StatNumber,
  Link,
  Stack,
} from '@chakra-ui/react';
import { Text } from '@outdoorly/text';
import { useQuery } from '@apollo/client';
import { Button, ButtonLink } from '@outdoorly/button';
import dayjs from 'dayjs';
import { GET_CREDIT_WALLET } from '../../lib/graphql/profile/queries';
import { Referrals } from './referrals';
import { makeNetworkStatus } from '../../hooks/make-network-status';

const creditTypeKeyMirror = {
  CODE_USED: 'CODE_USED',
  REFERRED_USER_MADE_PURCHASE: 'REFERRED_USER_MADE_PURCHASE',
  STAFF_ADJUSTMENT: 'STAFF_ADJUSTMENT',
  RETURN: 'RETURN',
  JOINED_AFTER_REFERRAL: 'JOINED_AFTER_REFERRAL',
  CREDIT_USED_IN_ORDER: 'CREDIT_USED_IN_ORDER',
  CREDIT_REFUND_FOR_CANCELLED_ORDER_OR_PART_OF_ORDER:
    'CREDIT_REFUND_FOR_CANCELLED_ORDER_OR_PART_OF_ORDER',
};

const creditTypeEnum = {
  [creditTypeKeyMirror.CODE_USED]: 'CODE',
  [creditTypeKeyMirror.REFERRED_USER_MADE_PURCHASE]: 'REFERRED',
  [creditTypeKeyMirror.STAFF_ADJUSTMENT]: 'ADJUSTMENT',
  [creditTypeKeyMirror.RETURN]: 'RETURN',
  [creditTypeKeyMirror.JOINED_AFTER_REFERRAL]: 'REFERRED',
  [creditTypeKeyMirror.CREDIT_USED_IN_ORDER]: 'ORDER',
  [creditTypeKeyMirror.CREDIT_REFUND_FOR_CANCELLED_ORDER_OR_PART_OF_ORDER]:
    'REFUND',
};

export const CREDITS_PER_DOLLAR = 100;

const CreditSummary = (props) => {
  const { type, date, amount, user, order } = props;
  const calculatedAmount = (amount / CREDITS_PER_DOLLAR).toFixed(2);
  const isNegative = amount < 0;
  const noAdditionalInfo =
    type === creditTypeKeyMirror.CODE_USED ||
    type === creditTypeKeyMirror.STAFF_ADJUSTMENT ||
    !creditTypeEnum[type];
  return (
    <Flex
      as={'li'}
      alignItems="center"
      justifyContent="space-between"
      my="20px"
    >
      <Box>
        <Flex mb="8px">
          <Text
            fontWeight={500}
            sx={{ paddingRight: noAdditionalInfo ? '0' : '7px' }}
          >
            {type === creditTypeKeyMirror.REFERRED_USER_MADE_PURCHASE
              ? user?.publicName
              : type === creditTypeKeyMirror.CREDIT_USED_IN_ORDER ||
                type === creditTypeKeyMirror.RETURN ||
                type ===
                  creditTypeKeyMirror.CREDIT_REFUND_FOR_CANCELLED_ORDER_OR_PART_OF_ORDER
              ? `Order #${order?.number}`
              : ''}
          </Text>
          <Text
            fontWeight={noAdditionalInfo ? '500' : 'normal'}
            sx={{ letterSpacing: noAdditionalInfo ? 'normal' : '0.1em' }}
            color={noAdditionalInfo ? 'scheme.textPrimary' : 'gray.800'}
          >
            {creditTypeEnum[type] || 'ADJUSTMENT'}
          </Text>
        </Flex>
        <Text color="scheme.textSecondary">
          {dayjs(date).format('MMM DD, YYYY')}
        </Text>
      </Box>
      <Box>
        <Text
          color={
            isNegative ? 'scheme.textMoneyNegative' : 'scheme.textMoneyPositive'
          }
          sx={{ textAlign: 'right', marginBottom: '8px' }}
        >{`${isNegative ? '-' : '+'}$${
          isNegative
            ? String(calculatedAmount)?.replace('-', '')
            : calculatedAmount
        }`}</Text>
        <Text size="sm" color="scheme.textSecondary">
          Credit{' '}
          {type === creditTypeKeyMirror.STAFF_ADJUSTMENT
            ? 'Adjusted'
            : isNegative
            ? 'Spent'
            : 'Earned'}
        </Text>
      </Box>
    </Flex>
  );
};

CreditSummary.propTypes = {
  amount: PropTypes.number,
  date: PropTypes.string,
  order: PropTypes.shape({
    number: PropTypes.string,
  }),
  type: PropTypes.string,
  user: PropTypes.shape({
    publicName: PropTypes.string,
  }),
};

export const ReferralIncentiveCard = (props) => {
  const { hideButton } = props;
  return (
    <Card
      as={Stack}
      direction="column"
      boxShadow="sm"
      background="red.50"
      mb={hideButton ? 0 : 2}
      py={6}
      justifyContent="center"
    >
      <Text as="h1" size="2xl" fontWeight="medium" mb={4}>
        Give $10, get $10!
      </Text>
      <Text color="scheme.textPrimary">
        Give your friends $10 off their first Outdoorly order. You’ll get a $10
        credit after they complete their first qualifying purchase of $50 or
        more.
      </Text>
      {!hideButton && (
        <Center>
          <ButtonLink
            href={'/invite'}
            backgroundColor="scheme.bgPrimary"
            color="scheme.textPrimary"
            mt={3}
            minW="205px"
          >
            Send Invites
          </ButtonLink>
        </Center>
      )}
    </Card>
  );
};

ReferralIncentiveCard.propTypes = {
  hideButton: PropTypes.bool,
};

export const Credits = () => {
  const { data, fetchMore, loading, networkStatus } = useQuery(
    GET_CREDIT_WALLET,
    {
      variables: {
        first: 5,
        after: null,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const { fetchingMore } = makeNetworkStatus(networkStatus);

  const [tabIndex, setCurrentTab] = useState(0);
  const hasNextPage =
    data?.me?.creditWallet.events.pageInfo.hasNextPage ?? false;
  const calculatedPointBalance = (
    data?.me?.creditWallet?.pointBalance / CREDITS_PER_DOLLAR
  ).toFixed(2);
  const calculatedTotalPoints = (
    data?.me?.creditWallet?.totalPointsEarned / CREDITS_PER_DOLLAR
  ).toFixed(2);
  const isCreditTab = tabIndex === 0;
  const isReferralTab = tabIndex === 1;
  const hasCreditHistory = data?.me?.creditWallet?.events?.edges?.length > 0;

  return (
    <>
      <ReferralIncentiveCard hideButton={false} />
      <Card boxShadow="sm" py={6}>
        <Text as="h1" size="lg" fontWeight="medium" mb={4}>
          Credits &amp; Referrals
        </Text>
        <Text color="scheme.textSecondary">
          Earn credits via referrals, purchases and achievements.{' '}
          <Link
            href="/invite"
            color="scheme.textSecondary"
            textDecoration="underline"
          >
            Invite friends
          </Link>{' '}
          and you&apos;ll both earn $10
        </Text>
        <Divider my={4} />
        <Tabs
          onChange={(index) => {
            setCurrentTab(index);
          }}
        >
          <TabList border={0}>
            <Box
              flexBasis="50%"
              borderRightColor="scheme.divider"
              borderRightWidth="1px"
            >
              <Tab
                flexBasis="50%"
                fontWeight="500"
                color="scheme.textSecondary"
                borderBottom="3px solid transparent"
                _focus={{ outline: 'none' }}
                _selected={{
                  color: 'scheme.textPrimary',
                  borderBottomColor: 'red.500',
                }}
                margin="auto"
              >
                Credit
              </Tab>
            </Box>
            <Box flexBasis="50%">
              <Tab
                fontWeight="500"
                color="scheme.textSecondary"
                borderBottom="3px solid transparent"
                _focus={{ outline: 'none' }}
                _selected={{
                  color: 'scheme.textPrimary',
                  borderBottomColor: 'red.500',
                }}
                margin="auto"
              >
                Referrals
              </Tab>
            </Box>
          </TabList>
        </Tabs>
      </Card>
      {isReferralTab && <Referrals />}
      {isCreditTab && (
        <>
          <Accordion
            allowMultiple
            background="black"
            borderRadius="xl"
            border="0"
            my={2}
          >
            <AccordionItem border="0">
              <AccordionButton>
                <Box flex="1" textAlign="left" py={2}>
                  <Skeleton isLoaded={!loading} w={loading ? 40 : '100%'}>
                    <StatNumber
                      color="white"
                      fontSize="3xl"
                      lineHeight="30px"
                    >{`$${calculatedPointBalance}`}</StatNumber>
                  </Skeleton>
                  <StatLabel color="white" fontSize="xs">
                    CURRENT BALANCE
                  </StatLabel>
                </Box>
                <AccordionIcon textAlign={'left'} color="white" />
              </AccordionButton>
              <AccordionPanel>
                <>
                  <Skeleton isLoaded={!loading} w={loading ? 40 : '100%'}>
                    <StatNumber
                      color="white"
                      fontSize="3xl"
                      lineHeight="30px"
                    >{`$${calculatedTotalPoints}`}</StatNumber>
                  </Skeleton>
                  <StatLabel color="white" fontSize="xs">
                    ALL TIME CREDIT
                  </StatLabel>
                </>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
          {hasCreditHistory && (
            <Card boxShadow="sm" py={1} my={2}>
              <ul>
                {data?.me?.creditWallet?.events?.edges.map((edge, index) => {
                  const { node } = edge;

                  return (
                    <CreditSummary
                      key={index}
                      type={node?.type}
                      date={node?.date}
                      amount={node?.amount}
                      user={node?.user}
                      order={node?.order}
                    />
                  );
                })}
              </ul>
            </Card>
          )}
          {hasNextPage && (
            <Center>
              <Button
                backgroundColor="scheme.bgPrimary"
                color="scheme.textPrimary"
                isLoading={fetchingMore}
                onClick={() =>
                  fetchMore({
                    variables: {
                      first: 5,
                      after: data?.me?.creditWallet.events.pageInfo.endCursor,
                    },
                  })
                }
              >
                Show more credit history
              </Button>
            </Center>
          )}
        </>
      )}
    </>
  );
};
